import { EMAIL_IDENTITY_CONFLICT_PREFIX } from '@rapid-response/shared'
import { Button } from '~/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '~/components/ui/dialog'

interface IdentityConflictDialogProps {
  /** Email of the currently-authenticated session (from the OIDC profile). */
  email: string | null | undefined
  /** Fires when the user clicks Sign out — wired to `auth.signoutRedirect()`. */
  onSignOut: () => void
}

/**
 * Rendered when `syncCurrentUserHandler` returns the email-identity-conflict
 * error: the Keycloak `sub` is new but the email already belongs to a
 * different row. The dialog stays modal (no close button) because the app
 * has no working session — clicking Sign out is the only path forward.
 *
 * Cookie-clearing instructions are inline rather than linked-out so a user
 * stuck in a redirect loop after sign-out can still fix it without leaving
 * this screen.
 */
export function IdentityConflictDialog({
  email,
  onSignOut,
}: Readonly<IdentityConflictDialogProps>) {
  return (
    <Dialog open>
      <DialogContent
        showCloseButton={false}
        onEscapeKeyDown={(e) => e.preventDefault()}
        onPointerDownOutside={(e) => e.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle>Did you log in with the correct user?</DialogTitle>
          <DialogDescription>
            This error typically happens if you logged in with another Keycloak
            user that has the same email.
          </DialogDescription>
        </DialogHeader>

        {email && (
          <p className="text-muted-foreground text-xs">
            Signed in as <span className="font-mono">{email}</span>
          </p>
        )}

        <div className="space-y-3 rounded-md border bg-muted/40 p-4 text-sm">
          <p className="font-medium">
            If signing out doesn't help, clear cookies for First Response and
            Keycloak, then try again.
          </p>

          <details className="space-y-1">
            <summary className="cursor-pointer text-xs font-medium text-muted-foreground hover:text-foreground">
              Chrome
            </summary>
            <ol className="ml-4 list-decimal space-y-1 text-xs text-muted-foreground">
              <li>Click the lock icon to the left of the address bar.</li>
              <li>Choose "Cookies and site data" then "Manage on-device site data".</li>
              <li>Remove every entry for this site, then repeat on your Keycloak domain.</li>
            </ol>
          </details>

          <details className="space-y-1">
            <summary className="cursor-pointer text-xs font-medium text-muted-foreground hover:text-foreground">
              Firefox
            </summary>
            <ol className="ml-4 list-decimal space-y-1 text-xs text-muted-foreground">
              <li>Click the lock icon to the left of the address bar.</li>
              <li>Choose "Clear cookies and site data".</li>
              <li>Repeat on your Keycloak domain.</li>
            </ol>
          </details>
        </div>

        <DialogFooter>
          <Button onClick={onSignOut}>Sign out</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

/**
 * Cheap predicate the root layout uses to decide whether to render the
 * dialog. Lives next to the component so the sentinel-prefix check has one
 * import-site rather than being duplicated at every callsite.
 */
export function isIdentityConflictError(error: Error | null | undefined): boolean {
  return error?.message?.startsWith(EMAIL_IDENTITY_CONFLICT_PREFIX) ?? false
}
